import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import humanizeString from 'humanize-string';

import { sourceShape } from '../../../propsShape';

import { Avatar, Capitalize, H5, Icon, Text } from '../../atoms';
import { DropdownMenuItem } from '../DropdownMenuItem';
import { Dropdown } from '../../organisms/Dropdown';

const StyledText = styled(Text)`
  white-space: pre-wrap;
  max-width: 400px;
`;

const NoWrapText = styled(Text)`
  white-space: nowrap;
`;

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
`;

const StyledDropdownMenuItem = styled(DropdownMenuItem)`
  align-items: flex-start;
`;

const StyledH5 = styled(H5)`
  padding: 8px;
`;

const ScrollableContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 300px;
  overflow-y: auto;
`;

const ColumnMentionsDropdownItem = forwardRef((props, ref) => {
  const { item } = props;

  if (item.data.views) {
    return (
      <Dropdown
        toggleMenuOnMouseEnter
        menuPlacement='right'
        menuStyle={{
          maxHeight: '600px',
          minHeight: '200px',
          overflowY: 'auto'
        }}
        menuOffset={32}
        toggleComponent={
          <DropdownMenuItem
            shouldCloseMenu={false}
            leftIcon={<Avatar width={20} src={item.data.source.integration.pictureUrl} />}
            rightIcon={<Icon size={20} name='chevron-right' color='var(--secondary-default)' />}
            onMouseDown={(evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
          >
            {item.value}
          </DropdownMenuItem>
        }
      >
        {() => (
          <>
            <StyledH5 color='var(--content-subtitle)'>Data tables</StyledH5>
            {item.data.views.map((view, index) => (
              <Dropdown
                key={`${view.value}-${index}`}
                menuStyle={{
                  width: 320,
                  zIndex: 'calc(var(--z-dropdown) + 2)'
                }}
                menuPlacement='right'
                menuOffset={32}
                toggleMenuOnMouseEnter
                isNested
                toggleComponent={
                  <DropdownMenuItem
                    key={`${index}-${view.value}`}
                    shouldCloseMenu={false}
                    onMouseDown={(evt) => {
                      evt.preventDefault();
                      evt.stopPropagation();
                    }}
                    leftIcon={
                      <Icon size={20} name='table' color='var(--action-tertiary-default)' />
                    }
                    rightIcon={
                      <Icon size={20} name='chevron-right' color='var(--secondary-default)' />
                    }
                  >
                    <Capitalize string={humanizeString(view.value)} />
                  </DropdownMenuItem>
                }
              >
                {() => (
                  <>
                    <StyledH5 color='var(--content-subtitle)'>Column names</StyledH5>
                    <ScrollableContainer>
                      {view.columns.map((column, i) => (
                        <StyledDropdownMenuItem
                          ref={ref}
                          key={`${i}-${column.value}`}
                          shouldCloseMenu={false}
                          onMouseDown={(evt) => {
                            evt.preventDefault();
                            evt.stopPropagation();
                          }}
                          onClick={(evt) =>
                            props.onClick(evt, {
                              value: column.value,
                              displayValue: column.column,
                              trigger: '@',
                              data: {
                                column: column.column,
                                columnDescription: column.columnDescription,
                                enumeratedValues: column.enumeratedValues,
                                view: column.view,
                                schema: column.schema,
                                source: column.source
                              }
                            })
                          }
                          leftIcon={
                            <StyledIcon
                              size={20}
                              name='column'
                              color='var(--action-tertiary-default)'
                            />
                          }
                        >
                          <H5 color='var(--content-body)'>{column.column}</H5>
                          <StyledText as='span' $small $color='var(--content-subtitle)'>
                            {column.columnDescription}
                          </StyledText>
                        </StyledDropdownMenuItem>
                      ))}
                    </ScrollableContainer>
                  </>
                )}
              </Dropdown>
            ))}
          </>
        )}
      </Dropdown>
    );
  }

  return (
    <DropdownMenuItem
      ref={ref}
      {...props}
      leftIcon={
        <Avatar
          width={20}
          src={props.item.data.source.integration.pictureUrl}
          alt={props.item.data.source.integration.name}
        />
      }
      rightIcon={
        <NoWrapText $color='var(--content-subtitle)'>
          in <Capitalize string={props.item.data.view} />
        </NoWrapText>
      }
    >
      {props.item.data.column}
    </DropdownMenuItem>
  );
});

// lexical-beautiful-mentions specific props
ColumnMentionsDropdownItem.propTypes = {
  'aria-label': PropTypes.string.isRequired,
  'aria-selected': PropTypes.bool.isRequired,
  item: PropTypes.shape({
    trigger: PropTypes.string,
    value: PropTypes.string.isRequired,
    displayValue: PropTypes.string.isRequired,
    data: PropTypes.shape({
      views: PropTypes.arrayOf(PropTypes.shape({})),
      schema: PropTypes.string,
      view: PropTypes.string,
      column: PropTypes.string,
      source: sourceShape
    }).isRequired
  }).isRequired,
  itemValue: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onMouseDown: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  tabIndex: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired
};

export { ColumnMentionsDropdownItem };
